import React, { useEffect, useState } from 'react';
import { Quote, PenLine } from 'lucide-react';
import { Stars } from '../components/Stars';
import { ReviewForm } from '../components/ReviewForm';
import { getReviews } from '../lib/api';

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = () => getReviews().then(setReviews).catch(() => setReviews([])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const avg = reviews.length
    ? (reviews.reduce((a, r) => a + (r.rating || 0), 0) / reviews.length)
    : 0;

  return (
    <div className="bg-alp-black min-h-screen">
      <div className="border-b border-alp-line">
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-16 text-center">
          <p className="text-alp-gold text-xs tracking-[0.3em] uppercase mb-3">Client Feedback</p>
          <h1 className="font-serif text-4xl md:text-5xl text-alp-cream mb-5 heading-glow">Reviews</h1>
          {reviews.length > 0 && (
            <div className="flex flex-col items-center gap-2" data-testid="reviews-summary">
              <Stars value={Math.round(avg)} size={22} />
              <p className="text-alp-gray text-sm">{avg.toFixed(1)} out of 5 &middot; {reviews.length} review{reviews.length === 1 ? '' : 's'}</p>
            </div>
          )}
          <button onClick={() => setShowForm((v) => !v)} data-testid="write-review-btn" className="btn-glow mt-7 inline-flex items-center gap-2 border border-alp-gold/50 text-alp-cream px-6 py-3 text-xs tracking-[0.2em] uppercase hover:border-alp-gold hover:text-alp-gold transition-colors">
            <PenLine size={14} /> {showForm ? 'Close' : 'Write a Review'}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="max-w-2xl mx-auto px-5 md:px-8 pt-10">
          <ReviewForm onSubmitted={() => { setShowForm(false); load(); }} />
        </div>
      )}

      <div className="max-w-5xl mx-auto px-5 md:px-8 py-14">
        {loading ? (
          <p className="text-center text-alp-gray py-20">Loading&hellip;</p>
        ) : reviews.length === 0 ? (
          <p className="text-center text-alp-gray py-20" data-testid="reviews-empty">No reviews yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {reviews.map((r) => (
              <article key={r.id} data-testid={`review-${r.id}`} className="border border-alp-line rounded-lg bg-alp-card/40 p-6 relative">
                <Quote size={28} className="text-alp-gold/20 absolute right-5 top-5" />
                <Stars value={r.rating} />
                {r.body ? <p className="text-alp-cream/85 text-sm leading-relaxed mt-4">{r.body}</p> : null}
                <div className="flex items-center justify-between mt-5 pt-4 border-t border-alp-line/60">
                  <div>
                    <p className="text-alp-cream text-sm font-medium">{r.name}</p>
                    {r.productName ? <p className="text-alp-gray text-xs">{r.productName}</p> : null}
                  </div>
                  {r.date ? <p className="text-alp-gray text-xs">{r.date}</p> : null}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
